// ── usePolyBalance — the Polymarket twin of the Kalshi balance readout ──
//
// Resolution order mirrors trading itself: the ORG credential pool when the
// org has poly keys (relay GET /api/polymarket/balance — account-level
// funder USDC), falling back to the browser-side personal connection's
// balance (polyAccountStore) when the relay reports the org unconfigured.
// "If the org key is enabled, then it's on the org key" (Brendan, Jul-8).
//
// Poll cadence is deliberately slow (30s, visibility-aware): balance moves
// on fills/settlements, not ticks, and the relay call is a live CLOB
// round-trip on the org's read quota. The result is cached MODULE-WIDE so
// the hook can mount on several always-on chrome surfaces (rail header,
// status bar, key-mode banner, VD widget) without multiplying that CLOB
// call — concurrent mounts share one fetch per TTL window.

import { useEffect, useRef, useState } from 'react';
import { getPolyBalanceViaRelay } from '@/lib/polymarket/relay';
import { usePolyAccountStore } from '@/stores/polyAccountStore';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';

const POLL_MS = 30_000;
// Shared-result window: slightly under the poll period so a single mount
// still refreshes every tick, while N mounts coalesce onto one fetch.
const SHARE_TTL_MS = 25_000;

type RelayResult = { usd: number } | 'unavailable';

let _cached: { at: number; result: RelayResult } | null = null;
let _inflight: Promise<RelayResult | null> | null = null;

async function fetchRelayShared(): Promise<RelayResult | null> {
  if (_cached && Date.now() - _cached.at < SHARE_TTL_MS) return _cached.result;
  if (_inflight) return _inflight;
  _inflight = getPolyBalanceViaRelay()
    .then((out): RelayResult | null => {
      if (out.ok) {
        _cached = { at: Date.now(), result: { usd: out.balance.collateralUsd } };
        return _cached.result;
      }
      if (out.code) {
        // Unavailable (org unconfigured / geoblock explainer) — cache it
        // too; no point re-asking every mount within the window.
        _cached = { at: Date.now(), result: 'unavailable' };
        return _cached.result;
      }
      // Transient error: no cache write — callers keep their last-good.
      return null;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

export interface PolyBalanceState {
  /** Funder USDC balance in dollars. Null until a source resolves. */
  usd: number | null;
  /** Which key path produced the number. Null while unresolved. */
  source: 'org' | 'personal' | null;
  /** True once the relay has ANSWERED that no org poly path exists (and no
   *  personal connection covers it) — distinguishes "no poly configured"
   *  from "still loading" for status indicators. */
  unavailable: boolean;
}

export function usePolyBalance(enabled: boolean = true): PolyBalanceState {
  const [relay, setRelay] = useState<RelayResult | null>(
    // Adopt the shared cache synchronously so late-mounting chrome doesn't
    // flash a loading state the app already knows the answer to.
    _cached && Date.now() - _cached.at < SHARE_TTL_MS ? _cached.result : null,
  );
  const personal = usePolyAccountStore((s) => s.funderBalance);
  const inFlight = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    const tick = () => {
      if (stopped || inFlight.current) return;
      inFlight.current = true;
      void fetchRelayShared()
        .then((result) => {
          if (stopped || result === null) return;
          setRelay(result);
        })
        .finally(() => {
          inFlight.current = false;
        });
    };
    tick();
    const stop = startVisibilityAwareInterval(tick, POLL_MS);
    return () => {
      stopped = true;
      stop();
    };
  }, [enabled]);

  if (relay && relay !== 'unavailable') return { usd: relay.usd, source: 'org', unavailable: false };
  if (personal) return { usd: personal.collateralUsd, source: 'personal', unavailable: false };
  return { usd: null, source: null, unavailable: relay === 'unavailable' };
}
